import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Checkbox, Divider, FormControlLabel, FormHelperText, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import MUIModal from "../../../common/MUIModal";
import { FormProvider, RHFSearchableSelectField, RHFTextField } from "../../../common/hook-form";
import { apiConstants, post } from "../../../common/constants";
import { toast } from "react-toastify";
import { getMerchant } from "../../../helpers/ReduxHelpers";

// Explicit allowlist of the merchant-facing modules offered in the Add User access
// picker. Driven as an allowlist (not a denylist) on purpose: the backend keeps
// adding internal/ops codes to auth/listmodules (PROPOSAL_CREATE/L1/ADMIN,
// AGREEMENT_APPROVER, ORDER_TERMINAL_RESET, MERCHANT_COURIER_ALLOCATION, …) and a
// denylist silently let each new one leak into the merchant form. New modules now
// have to be added here deliberately. "MERCHANT_ADMIN" is the "All Access" pseudo-module.
const MERCHANT_MODULES = [
  "DASHBOARD", "PERFORMANCE", "NDR", "ORDERS", "B2C_ACCESS", "PTL_ACCESS",
  "WEIGHT_DESC", "TOOLS", "FINANCE", "CT", "REPORTS", "RETURNS", "SETTINGS",
  "SHOW_AMOUNT", "VENDOR", "MERCHANT_ADMIN",
];

const AddUser = (props) => {
  const { show, handleClose, moduleList, loadUsers, currentUser, setCurrentId } = props;
  const [isLoading, setIsLoading] = React.useState(false);

  const moduleDescriptions = {
    "ALL": "All Modules",
    "DASHBOARD": "Dashboard Access",
    "PERFORMANCE": "Performance & Analytics",
    "NDR": "Non-Delivery Report (NDR) Module",
    "ORDERS": "Orders Module (D2C & PTL)",
    "B2C_ACCESS": "D2C Freight Related Access",
    "PTL_ACCESS": "PTL Related Access",
    "WEIGHT_DESC": "Weight Discrepancy Module",
    "TOOLS": "Tools & Utilities",
    "FINANCE": "Finance & Billing",
    "CT": "Courier Tracking",
    "REPORTS": "Reports & Analytics",
    "RETURNS": "Returns Management",
    "SETTINGS": "Settings & Configuration",
    "INTERNAL": "Internal Tools/Access",
    "MERCHANT_ADMIN": "All Access",
    "SHOW_AMOUNT": "Shipment Pricing view access rights",
    "USER_SAVE": "Create User",
    "USER_RO": "User Read-Only Permissions",
    "VENDOR": "Vendor Management"
  };

  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''

  const hideModal = () => {
    handleClose();
    setCurrentId(null);
  };

  const validation1 = {
    username: Yup.string().email().required(),
    accessModules: Yup.array().when('role', {
      is: 'admin',
      then: Yup.array().notRequired(),
      otherwise: Yup.array().min(1, 'Please select at least one module').required(),
    }),
  }

  const validation2 = {
    fullname: Yup.string().required(),
    role: Yup.string().required(),
  }

  const methods = useForm({

    resolver: yupResolver(Yup.object().shape(currentUser ? { ...validation1 } : { ...validation1, ...validation2 })),

    defaultValues: {
      username: "",
      fullname: "",
      role: "",
      accessModules: []
    },

    shouldUnregister: true

  });

  const { handleSubmit, setValue, watch } = methods;
  const role = watch('role');

  // Identifier for the "All Access" pseudo-module. Selecting it means the
  // user gets every other module too; unselecting any single module must
  // clear it so the checkbox always tells the truth.
  const ALL_ACCESS = "MERCHANT_ADMIN";

  // Only the merchant-facing modules (see MERCHANT_MODULES) are offered — anything
  // the backend returns that isn't allowlisted (internal/ops codes) is dropped.
  const allowedModules = React.useMemo(
    () => (moduleList || []).filter((v) => MERCHANT_MODULES.includes(v)),
    [moduleList],
  );

  // Reconcile the "All Access" pseudo-module with the rest of the selection.
  const accessModules = watch("accessModules") || [];
  const prevModulesRef = React.useRef([]);

  React.useEffect(() => {
    const prev = prevModulesRef.current;
    const curr = accessModules;
    const prevHasAll = prev.includes(ALL_ACCESS);
    const currHasAll = curr.includes(ALL_ACCESS);
    const nonAll = allowedModules.filter((m) => m !== ALL_ACCESS);

    let next = curr;

    if (!prevHasAll && currHasAll) {
      // User just checked "All Access" — auto-select every module.
      next = [ALL_ACCESS, ...nonAll];
    } else if (currHasAll) {
      // "All Access" is on; if any individual module is missing, the user
      // just unticked one — drop "All Access" so it stays honest.
      const allOthersSelected = nonAll.every((m) => curr.includes(m));
      if (!allOthersSelected) {
        next = curr.filter((m) => m !== ALL_ACCESS);
      }
    }

    const same =
      next === curr ||
      (next.length === curr.length && next.every((v) => curr.includes(v)));
    if (!same) {
      setValue("accessModules", next, { shouldValidate: true, shouldDirty: true });
      prevModulesRef.current = next;
    } else {
      prevModulesRef.current = curr;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessModules, allowedModules]);

  const onSubmitHandler = async (formData) => {

    try {

      setIsLoading(true);
      const isAdmin = formData.role === 'admin';
      const accessModules = isAdmin ? ['MERCHANT_ADMIN'] : formData.accessModules;
      const data = { ...formData, accessModules, "merchantOid": merchantId }
      const request = { ...data, email: formData.username };

      if (!currentUser) {
        request.role = [request.role]
        await post(`${apiConstants.BASE_LOCAL}${apiConstants.CREATE_USER}`, request);
      }

      const payload = { username: request.username, accessModules: request.accessModules };
      await post(`${apiConstants.BASE_LOCAL}${apiConstants.UPDATE_USER_ROLE}`, payload)

      loadUsers();
      hideModal();
      setCurrentId(null);

    } catch (err) {

      toast.error(err?.response?.data?.message || 'Oops Something went wrong!')

    } finally {
      setIsLoading(false);
    }

  }

  React.useEffect(() => {

    if (currentUser && setValue) {
      setValue('username', currentUser?.email);
      setValue('accessModules', currentUser?.accessList || []);
    }

  }, [setValue, currentUser])

  return (
    <MUIModal open={show || (currentUser ? true : false)} handleClose={hideModal} title={`${currentUser ? 'Edit' : 'Add New'} User`} maxWidth="lg">
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitHandler)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={currentUser ? 12 : 4}>
            <RHFTextField inputProps={{ readOnly: currentUser ? true : false }} name="username" label="Enter user email for login" size="medium" required variant="filled" />
          </Grid>
          {!currentUser && (
            <Grid item xs={12} md={4}>
              <RHFTextField name="fullname" label="Full Name" size="medium" variant="filled" required />
            </Grid>
          )}
          {!currentUser && (
            <Grid item xs={12} md={4}>
              <RHFSearchableSelectField name="role" label="Role" width="100%" required mt={0} mb={0} options={[{ label: 'Admin', value: 'admin' }, { label: 'User', value: 'user' }]} />
            </Grid>
          )}
          {!currentUser && (
            <Grid item xs={12} md={4}>
              <RHFTextField name="mobile" label="Mobile Number" size="medium" variant="filled" required />
            </Grid>
          )}
          <Grid item xs={12} md={12}>
            {!currentUser && role === 'admin' ? (
              <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eef0f3', bgcolor: '#fafbff' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                  Merchant Admin access will be assigned by default
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Admin users automatically get every module — no individual access selection needed.
                </Typography>
              </Box>
            ) : (
            <Controller
              name="accessModules"
              control={methods.control}
              render={({ field, fieldState: { error } }) => {
                const selected = field.value || [];
                const isSelected = (mod) => selected.includes(mod);
                const toggle = (mod) => {
                  field.onChange(
                    isSelected(mod) ? selected.filter((m) => m !== mod) : [...selected, mod],
                  );
                };
                const otherMods = allowedModules.filter((m) => m !== ALL_ACCESS);
                const allChecked = isSelected(ALL_ACCESS);
                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        Account Accessibility <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {selected.filter((m) => m !== ALL_ACCESS).length} of {otherMods.length} selected
                      </Typography>
                    </Box>
                    {allowedModules.includes(ALL_ACCESS) && (
                      <>
                        <FormControlLabel
                          control={<Checkbox checked={allChecked} onChange={() => toggle(ALL_ACCESS)} />}
                          label={<Typography sx={{ fontWeight: 600 }}>Select All (All Access)</Typography>}
                        />
                        <Divider sx={{ my: 1 }} />
                      </>
                    )}
                    <Grid container>
                      {otherMods.map((mod) => (
                        <Grid item xs={12} sm={6} key={mod}>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={isSelected(mod)}
                                onChange={() => toggle(mod)}
                              />
                            }
                            label={moduleDescriptions[mod] || mod}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    {error && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {error.message || 'Please select at least one module'}
                      </FormHelperText>
                    )}
                  </Box>
                );
              }}
            />
            )}
          </Grid>
          <Grid item xs={12} md={12}>
            <LoadingButton loading={isLoading} type="submit" variant="contained" required size="large">
              {currentUser ? 'Update User' : 'Create User'}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </MUIModal>
  )
};

export default AddUser;
